import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import ForumEntityClassSchema from "notifications/message-notifications/forum-info/forum-entity-class-schema";
import { z } from "zod";

export type TopicJSON = z.infer<typeof TopicSchema.TopicJSONZ>;
export type TopicParams = z.infer<typeof TopicSchema.TopicZ>;

export default class TopicSchema {
  public static readonly TopicJSONZ = z.object({
    entityClass: ForumEntityClassSchema.ForumEntityClassJSONZ.nullish(),
    entityId: GuidSchema.GuidJSONZ.nullish(),
    context: z.string().nullish(),
  });

  public static readonly TopicZ = z.object({
    entityClass: z.instanceof(ForumEntityClass).optional(),
    entityId: z.instanceof(Guid).optional(),
    context: z.string().optional(),
  });

  public static validate(values: unknown): TopicParams {
    return parseOrThrow(TopicSchema.TopicZ, values, {
      logError: true,
      schemaName: "TopicSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): TopicJSON {
    return parseOrThrow(TopicSchema.TopicJSONZ, json, {
      logError: true,
      schemaName: "TopicSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is TopicParams {
    try {
      TopicSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is TopicJSON {
    try {
      TopicSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): TopicParams {
    const parsed = parseOrThrow(TopicSchema.TopicJSONZ, json, {
      logError: true,
      schemaName: "TopicSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return TopicSchema.codec.decode(parsed);
  }

  public static serialize(params: TopicParams): TopicJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = TopicSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return TopicSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(TopicSchema.TopicJSONZ, TopicSchema.TopicZ, {
    decode: (data) => ({
      entityClass: ForumEntityClass.fromJSON(data.entityClass) ?? undefined,
      entityId: Guid.fromJSON(data.entityId) ?? undefined,
      context: data.context ?? undefined,
    }),
    encode: (topic) => ({
      entityClass: topic.entityClass?.toJSON(),
      entityId: topic.entityId?.toJSON(),
      context: topic.context,
    }),
  });
}
